import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(useGSAP, ScrollTrigger);

// Mark meaningful blocks with data-reveal. Keep this on children of Isotope
// items and slider tracks, whose transforms belong to those libraries.
export function usePageReveal(scope, enabled = true) {
  useGSAP(() => {
    const root = scope.current;
    if (!root || !enabled) return;

    const media = gsap.matchMedia();
    media.add("screen and (prefers-reduced-motion: no-preference)", context => {
      const seen = new WeakSet();
      let frame;
      let active = true;
      const refresh = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => ScrollTrigger.refresh());
      };

      // context.add also records animations created later by the observer.
      context.add("reveal", element => {
        if (seen.has(element) || !element.getClientRects().length) return;
        seen.add(element);
        gsap.fromTo(element, { autoAlpha: 0, y: 32 }, {
          autoAlpha: 1,
          y: 0,
          duration: 1.3,
          delay: Number(element.dataset.revealDelay || 0),
          ease: "power2.out",
          clearProps: "opacity,visibility,transform",
          scrollTrigger: {
            trigger: element,
            start: "top 92%",
            once: true,
          },
        });
      });

      const discover = node => {
        if (!(node instanceof Element)) return;
        if (node.matches("[data-reveal]")) context.reveal(node);
        node.querySelectorAll("[data-reveal]").forEach(context.reveal);
      };
      discover(root);
      const observer = new MutationObserver(records => {
        let addedContent = false;
        records.forEach(record => record.addedNodes.forEach(node => {
          if (node instanceof Element) {
            discover(node);
            addedContent = true;
          }
        }));
        if (addedContent) refresh();
      });
      observer.observe(root, { childList: true, subtree: true });

      const resizeObserver = new ResizeObserver(() => {
        // Responsive blocks can become visible after a breakpoint change.
        discover(root);
        refresh();
      });
      resizeObserver.observe(root);
      root.addEventListener("load", refresh, true);
      root.addEventListener("portfolio:layout", refresh);
      document.fonts?.ready.then(() => { if (active) refresh(); });
      refresh();

      return () => {
        active = false;
        observer.disconnect();
        resizeObserver.disconnect();
        root.removeEventListener("load", refresh, true);
        root.removeEventListener("portfolio:layout", refresh);
        cancelAnimationFrame(frame);
      };
    }, root);
    return () => media.revert();
  }, { scope, dependencies: [enabled], revertOnUpdate: true });
}
